import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  DvButtonComponent,
  DvDialogEvent,
  DvFormComponent,
  DvFormUtil,
  DvFormWrapperComponent,
  FormOf,
} from '@devinco/ui/dds';
import { DvSavePanelModule, DvTabBase } from '@devinco/ui/legacy';
import { TranslateModule } from '@ngx-translate/core';
import { RestApiService } from '@core/services/rest-api.service';
import { RouteNetworkInformationTabFormsService } from './route-network-information-tab-forms.service';
import { FormTranslationDirective } from '@shared/dv-form/directives/dv-form-translate.directive';
import { VtFormElement } from '@shared/dv-form/interfaces/form.element';
import { DialogService } from '@core/services/dialog.service';
import {
  AutoZoomFitParams,
  FullScreenMapProps,
  MapInput,
  MapLayer,
} from '@features/map/interfaces';
import { FullScreenMapComponent } from '@features/map/full-screen-map/full-screen-map.component';
import { MapId } from '@features/map/constants/map-id.enum';
import {
  BehaviorSubject,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  outputToObservable,
  takeUntilDestroyed,
  toSignal,
} from '@angular/core/rxjs-interop';
import { RouteNetwork } from '@shared/interfaces/route-network';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import FormUtils from '@shared/dv-form/utils/form.utils';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { EventBusService } from '@shared/services/event-bus/event-bus.service';
import { EventKey } from '@shared/constants/event-bus/event-key';
import { RouteNetworkEditHelperService } from './route-network-edit-helper.service';
import { SupportUserService } from '@shared/services/support-user.service';
import { ClientRouteNetworkStoreService } from './client-route-network-store.service';
@Component({
  selector: 'app-route-network-information-tab',
  imports: [
    DvFormComponent,
    DvSavePanelModule,
    TranslateModule,
    FormTranslationDirective,
    DvFormWrapperComponent,
    DvButtonComponent,
  ],
  templateUrl: './route-network-information-tab.component.html',
  styles: `
    .route-network-edit-buttons {
      display: flex;
      flex-direction: row;
      gap: var(--dv-spacing-150);
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [RouteNetworkEditHelperService],
})
export class RouteNetworkInformationTabComponent
  extends DvTabBase
  implements OnInit, PendingChanges, OnDestroy
{
  private tabFormsService = inject(RouteNetworkInformationTabFormsService);
  private api = inject(RestApiService);
  private dialog = inject(DialogService);
  private splitterHelperService = inject(SplitterWithGridHelperService);
  private eventBusService = inject(EventBusService);
  private networkEditHelperService = inject(RouteNetworkEditHelperService);
  private supportUserService = inject(SupportUserService);
  private customerRouteNetworksStore = inject(ClientRouteNetworkStoreService);

  readonly NEW_ENTITY_ROUTE_PARAM = NEW_ENTITY_ROUTE_PARAM;

  formGroup: FormGroup<FormOf<RouteNetwork>>;
  elements: VtFormElement[];

  /** toggle for map automatic zoom fit into the screen */
  private routeNetworkEditZoomFit$ = new BehaviorSubject({
    doAutoFit: true,
  } as AutoZoomFitParams);

  /** merge triggers initial emission */
  private editRouteNetworkMarkerInput$: Observable<MapInput[]> = merge(
    this.eventBusService.on(EventKey.RouteNetworkRefetchTriggered),
    of(null)
  ).pipe(
    switchMap(event => {
      const eventData = event as { doAutoFit?: boolean } | undefined | null;
      if (eventData) {
        this.toggleZoomFit(eventData.doAutoFit);
      }

      this.networkEditHelperService.setRouteSegmentTooltips(false);
      this.networkEditHelperService.setRouteSegmentLineTooltips(false);

      return this.customerRouteNetworksStore.getRouteSegments(
        this.entityIdNumber,
        true,
        true,
        this.routeNetworkEditExtendedEnabled()
          ? this.onPolylineRightClick
          : undefined,
        true
      );
    })
  );

  private isSupportUser = toSignal(this.supportUserService.isSupportUser());

  private routeNetworkEditExtendedEnabled = computed(() => {
    const isSupportUser = this.isSupportUser();
    return isSupportUser;
  });

  get entityIdNumber(): number {
    return Number(this.entityId);
  }

  ngOnInit(): void {
    this.elements = this.tabFormsService.getElements();
    this.formGroup = DvFormUtil.buildFormGroup(this.elements);
  }

  ngOnDestroy(): void {
    this.customerRouteNetworksStore.clearRouteNetworkStore();
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  override onBaseActions(): void {}
  override onEntityUpdate(): void {
    const requestKeys = FormUtils.getKeysForRequest(this.elements);

    this.api
      .getInfo<RouteNetwork>(Entity.RouteNetworkWeb, this.entityId, [
        ...requestKeys,
        'RouteNetworkID',
      ])
      .subscribe(res => {
        this.formGroup.markAsPristine();
        this.formGroup.patchValue(res);
      });
  }

  override onEntityCreate(): void {
    this.formGroup.reset();
    this.formGroup.markAsPristine();

    /** disable controls that are attached to POI/network edit buttons */
    const { CustomerID, IsPublic } = this.formGroup.controls;
    CustomerID?.disable();
    IsPublic?.disable();
  }

  onSave(): void {
    this.performSave().subscribe();
  }

  editPoi(): void {
    this.customerRouteNetworksStore
      .getRouteSegments(this.entityIdNumber, false)
      .pipe(take(1))
      .subscribe(nonEditableSegments => {
        const { Latitude, Longitude } = this.formGroup.controls;

        const markerInput$ = new BehaviorSubject([
          ...nonEditableSegments,
          ...this.networkEditHelperService.constructMarkerInputValueFromCoords(
            Latitude?.value,
            Longitude?.value
          ),
        ]);
        const data: FullScreenMapProps = {
          id: MapId.RouteNetworkMarkerMap,
          markers$: markerInput$,
          addNewMarkerOnDblClick: !Latitude?.value && !Longitude?.value,
          resetAllMarkersOnClick: true,
        };

        this.dialog
          .open<FullScreenMapProps, FullScreenMapComponent>({
            size: 'full',
            contentComponent: FullScreenMapComponent,
            hideHeader: true,
            hideFooter: true,
            data,
            onReady: event => {
              if (!event.context.contentComponent) return;

              event.context.contentComponent.fullScreenMapReady$
                .pipe(
                  filter(map => !!map),
                  switchMap(mapInstance =>
                    merge(outputToObservable(mapInstance.markerUpdated))
                  ),
                  switchMap(event => {
                    Latitude?.setValue(event.lat);
                    Longitude?.setValue(event.lng);

                    if (event.isNew) {
                      markerInput$.next([
                        ...nonEditableSegments,
                        ...this.networkEditHelperService.constructMarkerInputValueFromCoords(
                          Latitude?.value,
                          Longitude?.value
                        ),
                      ]);
                    }
                    return this.api.upsert(Entity.RouteNetwork, this.entityId, {
                      Latitude: event.lat,
                      Longitude: event.lng,
                    });
                  }),
                  takeUntilDestroyed(event.context.contentComponent.destroyRef)
                )
                .subscribe();
            },
          })
          .closed.pipe(
            take(1),
            tap(() =>
              this.customerRouteNetworksStore.triggerRouteNetworkRefetchIfNeeded()
            )
          )
          .subscribe();
      });
  }

  editRouteNetwork(): void {
    this.toggleZoomFit();
    const data = this.buildRouteNetworkMapData();
    this.openRouteNetworkDialog(data);
  }

  private toggleZoomFit(doAutoFit = true): void {
    this.routeNetworkEditZoomFit$.next({ doAutoFit });
  }

  private openRouteNetworkDialog(data: FullScreenMapProps): void {
    this.dialog
      .open<FullScreenMapProps, FullScreenMapComponent>({
        size: 'full',
        contentComponent: FullScreenMapComponent,
        hideHeader: true,
        hideFooter: true,
        data,
        onReady: (
          e: DvDialogEvent<FullScreenMapProps, FullScreenMapComponent>
        ) =>
          this.networkEditHelperService.onMapDialogReady(
            e,
            !!this.routeNetworkEditExtendedEnabled(),
            this.entityIdNumber,
            this.refetchRouteNetwork
          ),
      })
      .closed.pipe(
        take(1),
        tap(() => {
          this.customerRouteNetworksStore.triggerRouteNetworkRefetchIfNeeded();
        })
      )
      .subscribe();
  }

  private buildRouteNetworkMapData(): FullScreenMapProps {
    const { Latitude, Longitude } = this.formGroup.controls;
    const hasCoords = !!Latitude?.value && !!Longitude?.value;

    return {
      id: MapId.RouteNetworksMap,
      markers$: this.editRouteNetworkMarkerInput$,
      enableTripImport: this.routeNetworkEditExtendedEnabled(),
      autoZoomFit$: this.routeNetworkEditZoomFit$,
      enablePolylineAdd: true,
      center: hasCoords
        ? { lat: Latitude.value, lng: Longitude.value }
        : undefined,
      zoom: hasCoords ? 10 : undefined,
      mapRefreshFn: () => this.refetchRouteNetwork(),
    };
  }

  private refetchRouteNetwork = (): void => {
    this.toggleZoomFit(false);
    this.eventBusService.emit({ key: EventKey.RouteNetworkRefetchTriggered });
  };

  private performSave(): Observable<boolean> {
    const payload = this.formGroup.getRawValue();
    const isCreating = this.entityId === NEW_ENTITY_ROUTE_PARAM;
    return this.api
      .upsert(Entity.RouteNetwork, isCreating ? 0 : this.entityId, payload)
      .pipe(
        tap(id => {
          if (isCreating) {
            if (!id) return;
            this.splitterHelperService.addNewlyCreatedItemToGrid(
              Entity.RouteNetworkWeb,
              id
            );
          } else {
            this.splitterHelperService.refreshRowData(this.entityId);
          }

          this.formGroup.markAsPristine();
        }),
        map(() => true)
      );
  }

  private onPolylineRightClick = (event: L.LeafletMouseEvent): void => {
    this.dialog
      .open({
        title: 'General.Dialog.Delete.Title',
        contentText: 'RouteNetwork.NetworkSegmentDeleteText',
        confirmText: 'General.Confirm',
        cancelText: 'General.Cancel',
        size: 'md',
        type: 'error',
      })
      .closed.pipe(
        switchMap(({ source }) => {
          if (source !== 'confirm') return of();

          const extraData = (event.target as MapLayer).extraData;
          const routeSegmentId = extraData?.polylineSegmentId;

          if (!routeSegmentId) return of();
          return this.api
            .deleteCustom(`Route/DeleteSegment/${routeSegmentId}`)
            .pipe(
              tap(() => {
                this.toggleZoomFit(false);
                this.eventBusService.emit({
                  key: EventKey.RouteNetworkRefetchTriggered,
                });
                this.customerRouteNetworksStore.registerRouteNetworkChange(
                  this.entityIdNumber
                );
              })
            );
        }),
        take(1)
      )
      .subscribe();
  };
}
